import { useCallback, useEffect, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import { TablePageLayout } from "../../../../../components/TablePage/TablePageLayout";
import { TablePlaceholder } from "../../../../../util/util";
import { ApprovalsListFilters } from "./ApprovalsListFilters";
import { buildApprovalsListColumns } from "./approvalsListColumns";
import { EMPTY_APPROVALS_LIST_FILTERS, chipIds } from "./approvalsList.types";
import type { ApprovalsListTableProps } from "./approvalsList.types";
import { useSkuIdColumnHeader } from "../hooks/useSkuIdColumnHeader";

function hasActiveFilters(
  filters: ApprovalsListTableProps["filters"],
  clearedFilters: ApprovalsListTableProps["filters"]
): boolean {
  const keys: Array<
    Exclude<keyof ApprovalsListTableProps["filters"], "item_details">
  > = [
    "status",
    "assignee",
    "approver",
    "stage",
    "requested_by",
    "approval_type",
  ];
  if (
    keys.some(
      (key) =>
        chipIds(filters[key]).join("|") !==
        chipIds(clearedFilters[key]).join("|")
    )
  ) {
    return true;
  }
  const detailKeys = new Set([
    ...Object.keys(filters.item_details ?? {}),
    ...Object.keys(clearedFilters.item_details ?? {}),
  ]);
  for (const key of detailKeys) {
    if (
      chipIds(filters.item_details?.[key] ?? []).join("|") !==
      chipIds(clearedFilters.item_details?.[key] ?? []).join("|")
    ) {
      return true;
    }
  }
  return false;
}

/**
 * Shared approvals table with search + API-backed filters for product Approvals
 * tab and the Approvals dashboard.
 */
export function ApprovalsListTable({
  variant,
  approvals,
  pagination,
  isLoading,
  error,
  onRowClick,
  onPageChange,
  onPerPageChange,
  perPage,
  offset,
  searchQuery,
  onSearchChange,
  onClearSearch,
  searchPlaceholder,
  skuIdHeader,
  filters,
  onFiltersChange,
  apiFilters,
  listDetails,
  totalApprovalsCount,
  onAssigneeUpdated,
  clearedFilters = EMPTY_APPROVALS_LIST_FILTERS,
}: ApprovalsListTableProps) {
  const { t } = useTranslation();
  const perPageItems = [10, 20, 50];
  const defaultSkuHeader = useSkuIdColumnHeader({ t });
  const resolvedSkuHeader = skuIdHeader ?? defaultSkuHeader;

  // Prefer /filters list_details; fall back to list response while facets load.
  const resolvedListDetails = apiFilters?.list_details ?? listDetails;

  const handleRowClick = useCallback(
    (e: React.MouseEvent<HTMLTableRowElement>) => {
      const approvalId = e.currentTarget.id;
      const approval = approvals.find((a) => a.id === approvalId);
      if (approval) {
        onRowClick(approval, e);
      }
    },
    [approvals, onRowClick]
  );

  const columns = useMemo(
    () =>
      buildApprovalsListColumns({
        t,
        variant,
        skuIdHeader: resolvedSkuHeader,
        listDetails: resolvedListDetails,
        onAssigneeUpdated,
        searchQuery,
      }),
    [
      t,
      variant,
      resolvedSkuHeader,
      resolvedListDetails,
      onAssigneeUpdated,
      searchQuery,
    ]
  );

  const scrollRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    scrollRef.current?.scrollTo({ top: 0 });
  }, [offset, perPage]);

  const tablePagination = useMemo(
    () =>
      pagination && pagination.total > perPage
        ? {
            perPage,
            pageCount: Math.ceil(pagination.total / perPage),
            pageIndex: pagination.offset / perPage + 1,
          }
        : undefined,
    [pagination, perPage]
  );

  const matchingCount = pagination?.total ?? 0;
  const totalCount = totalApprovalsCount ?? matchingCount;
  const showClearFilters = hasActiveFilters(filters, clearedFilters);

  return (
    <TablePageLayout
      searchQuery={searchQuery}
      searchPlaceholder={searchPlaceholder ?? t("Search")}
      onSearchChange={onSearchChange}
      onSearchClear={onClearSearch}
      filtersSlot={
        <ApprovalsListFilters
          filters={filters}
          onFiltersChange={onFiltersChange}
          apiFilters={apiFilters}
          listDetails={listDetails}
        />
      }
      showClearAll={showClearFilters}
      onClearAll={() => onFiltersChange(clearedFilters)}
      clearAllLabel={t("Clear Filters")}
      totalCount={totalCount > 0 ? totalCount : undefined}
      searchFilterCount={matchingCount < totalCount ? matchingCount : undefined}
      totalCountLabel={t("approvals")}
      perPageItems={perPageItems}
      perPage={perPage}
      onPerPageChange={onPerPageChange}
      columns={columns}
      data={approvals}
      isLoading={isLoading}
      error={error}
      handleSort={() => {}}
      rowClick={handleRowClick}
      Placeholder={<TablePlaceholder message={t("No items to show.")} />}
      pagination={tablePagination}
      offset={offset}
      onPageChange={onPageChange}
      scrollRef={scrollRef}
      containerStyle={{ marginTop: 0 }}
    />
  );
}
